import { inject, Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { AbstractTool } from '../../../../cross-team-shared/annotation/shared/abstract-tool';
import { ShapeState2D } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { BrowserControlService } from '../../../../cross-team-shared/misc-services/browser-control.service';
import {
  MouseButton,
  ShapeEventInfo,
} from '../../../../cross-team-shared/misc-services/events/mouse-event.service';
import { AbstractSensorContentState2D } from '../../state/abstract-sensor-content-state-2d';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import { ImageBoundsService } from '../edit/image-bounds.service';

export abstract class AbstractTool2D<
  A extends AbstractSensorContentState2D = AbstractSensorContentState2D,
  T extends AbstractSensorContentStore2D<A> = AbstractSensorContentStore2D<A>,
> extends AbstractTool<A, T> {
  protected browserControlService = inject(BrowserControlService);
  protected imageBondsService = inject(ImageBoundsService);
  protected injector = inject(Injector);
  private mousemoveSinceLastRightMousedown = false;

  override async onActivate() {
    await super.onActivate();
    this.onEvent('container', 'mouseup', this.onContainerRightMouseUp, {
      button: MouseButton.Right,
    });
    this.onEvent('shape', 'mouseup', this.onShapeRightMouseUp, {
      button: MouseButton.Right,
    });
    this.onEvent('container', 'mousedown', this.onRightMouseDown, {
      button: MouseButton.Right,
    });
    this.setUpMousemoveSubscription();
  }

  getSnappedImagePoint() {
    return this.imageBondsService.snapToImage(this.getImagePoint());
  }

  getBoundedImagePoint() {
    return this.imageBondsService.boundPoints(this.getImagePoint());
  }

  protected onShapeRightMouseUp(
    event: MouseEvent,
    { shapeStore }: ShapeEventInfo,
    source = 'select_tool_2d',
  ) {
    if (!this.mousemoveSinceLastRightMousedown) {
      this.modifySelection(shapeStore, event, source);
      this.shellConnector.openShapeContextMenu([event.clientX, event.clientY]);
      // stop propagation here is to stop the canvas context menu from opening
      event.stopPropagation();
    }
  }

  protected modifySelection(
    shapeStore: Store<ShapeState2D>,
    event: MouseEvent,
    source: string,
  ) {
    const index = shapeStore.state().index;
    const modifierDown =
      event[this.browserControlService.getPlatformModifier()];
    this.shellConnector.modifyShapeSelection(
      [index],
      modifierDown ? 'toggle' : 'set',
      { source },
    );
  }

  protected onContainerRightMouseUp(event: MouseEvent) {
    if (!this.mousemoveSinceLastRightMousedown) {
      this.shellConnector.openCanvasContextMenu([event.clientX, event.clientY]);
    }
  }

  protected getImagePoint() {
    return this.store.state().mouse.imagePoint;
  }

  private onRightMouseDown() {
    this.mousemoveSinceLastRightMousedown = false;
  }

  private setUpMousemoveSubscription() {
    this.onEvent('container', 'mousemove', () => {
      this.mousemoveSinceLastRightMousedown = true;
    });
  }
}
